import { DialogRef } from '@angular/cdk/dialog';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StockService } from 'src/app/feature/services';
import { StockCreateSize, TalcColor } from 'src/app/shared';
import { StockItem } from 'src/app/core';
import { SubSink } from 'subsink';
import { merge, Subject } from 'rxjs';
import { AreYouSureComponent } from 'src/app/feature/components';

interface AdminCellAddDialogData {
  target?: TargetType;
  editMode?: boolean;
  pointer?: string;
}

type TargetType = 'binding' | 'type' | 'color' | 'bindingSalaryFee';

@Component({
  selector: 'admin-cellar-add',
  templateUrl: './admin-cellar-add.component.html',
  styleUrls: ['./admin-cellar-add.component.sass'],
})
export class AdminCellarAddComponent implements OnInit {
  public subsink = new SubSink();
  public target: TargetType = 'type';
  public pointer: string | undefined;
  public typeStocks: StockItem[] = [];
  public isEditMode: boolean = false;
  public typeFormGroup: FormGroup;
  public typeCoverFormGroup: FormGroup;
  public bindingFormGroup: FormGroup;
  public talcColorFormGroup: FormGroup;
  public bindingSalaryFeeFormGroup: FormGroup;
  public priceRangeFormArray: FormArray;

  public refreshSubject = new Subject();

  ngOnInit(): void { }

  constructor(
    @Inject(MAT_DIALOG_DATA) dialogData: AdminCellAddDialogData,
    private dialogReference: DialogRef,
    private stockService: StockService,
    private snackBar: MatSnackBar,
    private dialogBox: MatDialog
  ) {
    const priceValidators = [Validators.required, Validators.min(1)];

    if (dialogData && dialogData.target) {
      this.target = dialogData.target;
    }
    this.pointer = dialogData?.pointer;
    this.isEditMode = !!dialogData?.editMode;

    /**
     * Define Form groups
     */

    this.typeFormGroup = new FormBuilder().group({
      name: [this.isEditMode && this.pointer ? this.pointer : '', [Validators.required]],
      bw1: ['', priceValidators],
      bw2: ['', priceValidators],

      c1: ['', priceValidators],
      c2: ['', priceValidators],

      hc1: ['', priceValidators],
      hc2: ['', priceValidators],
    });
    this.typeCoverFormGroup = new FormBuilder().group({
      name: [this.isEditMode && this.pointer ? this.pointer : '', [Validators.required]],
      price: ['',priceValidators]
    });
    this.priceRangeFormArray = new FormBuilder().array([])
    this.bindingFormGroup = new FormBuilder().group({
      name: [
        {
          value: this.isEditMode && this.pointer ? this.pointer : '',
        },
        [Validators.required],
      ],
      price: [null, [Validators.required, Validators.min(0)]],
      priceRanges: this.priceRangeFormArray,
      amount: ['1'],
      infinite: [false],
      needsTalc: [false],
    });

    this.talcColorFormGroup = new FormBuilder().group({
      name: ['', [Validators.required]],
      colorCode: ['#fff', Validators.required],
    });

    this.bindingSalaryFeeFormGroup = new FormBuilder().group({
      bindSalaryFee: ['', [Validators.required]],
    });

    /**
     * Update if target is passed.
     */

    if (this.target === 'type' && this.pointer) {
      this.subsink.sink = this.stockService
        .getByName(this.pointer)
        .subscribe((stockItems) => {
          this.typeStocks = stockItems;
          if (stockItems.length == 1) {
            const information = {name:stockItems[0].name , price:stockItems[0].price}
            this.typeCoverFormGroup.setValue(information);
          } else {
            const information = this.extractInformationFromType();
            this.typeFormGroup.setValue(information);
          }
        });
    }

    if (this.target === 'binding' && this.pointer) {
      this.subsink.sink = this.stockService.getById(+this.pointer).subscribe({
        next: ({ name, price, amount, infinite, needsTalc, priceRanges }) => {
          this.bindingFormGroup.patchValue({
            name,
            price: price,
            amount: amount,
            infinite,
            needsTalc,
          });
          if (priceRanges.length > 0) {
            priceRanges.forEach(range => {
              this.priceRangeFormArray.push(this.createPriceRange(range));
            });
          }
        },
      });
    }

    if (this.target === 'bindingSalaryFee' && this.pointer) {
      this.subsink.sink = this.stockService.getBindSalaryFee(this.pointer).subscribe({
        next: ({ bindSalaryFee }) => {
          this.bindingSalaryFeeFormGroup.patchValue({
            bindSalaryFee
          });
          // if(priceRanges.length> 0){
          //   priceRanges.forEach(range => {
          //     this.priceRangeFormArray.push(this.createPriceRange(range));
          //   });
          //           }
        },
      });
    }
    if (this.target === 'color' && this.pointer) {
      this.subsink.sink = this.stockService
        .getTalcColorById(this.pointer)
        .subscribe(({ name, colorCode }) => {
          this.talcColorFormGroup.setValue({
            name,
            colorCode,
          });
        });
    }
  }
  get priceRanges(): FormArray {
    return this.bindingFormGroup.get('priceRanges') as FormArray;
  }

  createPriceRange(range?: any): FormGroup {
    return new FormBuilder().group({
      min: [range?.min || '', Validators.required],
      max: [range?.max || '', Validators.required],
      price: [range?.price || '', [Validators.required, Validators.min(0)]],
    });
  }
  addPriceRange() {
    this.priceRanges.push(this.createPriceRange());
  }

  removePriceRange(index: number) {
    this.priceRanges.removeAt(index);
  }
  addSize() {
    this.subsink.sink = this.stockService
      .addSize(this.typeFormGroup.value)
      .subscribe({
        next: (stockItems) => {
          this.dialogReference.close();
        },
      });
  }

  updateSize() {
    this.subsink.sink = this.stockService
      .updateSize(this.typeFormGroup.value)
      .subscribe({
        next: (stockItems: StockItem[]) => {
          this.snackBar.open('بروز رسانی با موفقیت انجام شد', '', {
            duration: 1500,
          });

          const information = this.extractInformationFromType(stockItems);

          this.typeFormGroup.markAsPristine();
          this.typeFormGroup.setValue(information);
        },
      });
  }
  updateCover() {
    this.subsink.sink = this.stockService
      .updateSize(this.typeCoverFormGroup.value)
      .subscribe({
        next: (stockItems: StockItem[]) => {
          this.snackBar.open('بروز رسانی با موفقیت انجام شد', '', {
            duration: 1500,
          });

          // const information = this.extractInformationFromType(stockItems);

          // this.typeFormGroup.markAsPristine();
          // this.typeFormGroup.setValue(information);
        },
      });
  }

  extractInformationFromType(typeStocks?: StockItem[]): StockCreateSize {
    const name = this.typeStocks[0].name;
    const typesStocks = typeStocks ? typeStocks : this.typeStocks;
    return {
      name,
      bw1: +typesStocks
        .filter((item) => item.name === name && item.type == '1bw')
        .map((item) => item.price)
        .at(0)!,
      bw2: +typesStocks
        .filter((item) => item.name === name && item.type == '2bw')
        .map((item) => item.price)
        .at(0)!,

      c1: +typesStocks
        .filter((item) => item.name === name && item.type == '1c')
        .map((item) => item.price)
        .at(0)!,

      c2: +typesStocks
        .filter((item) => item.name === name && item.type == '2c')
        .map((item) => item.price)
        .at(0)!,

      hc1: +typesStocks
        .filter((item) => item.name === name && item.type == '1hc')
        .map((item) => item.price)
        .at(0)!,

      hc2: +typesStocks
        .filter((item) => item.name === name && item.type == '2hc')
        .map((item) => item.price)
        .at(0)!,
    };
  }

  addBinding() {
    const request = { ...this.bindingFormGroup.value, isType: true };
    if (request.price) request.price = request.price + '';
    // if ()
    this.subsink.sink = this.stockService
      .addBinding(request)
      .subscribe((stockItems) => {
        this.snackBar.open('با موفقیت اضافه شد', '', { duration: 1500 });
        this.bindingFormGroup.reset();
      });
  }

  updateBinding() {
    this.subsink.sink = this.stockService
      .updateBinding({
        ...this.bindingFormGroup.value,
        id: parseInt(this.pointer!),
        price: this.bindingFormGroup.controls['price'].value + '',
        amount: this.bindingFormGroup.controls['amount'].value ? this.bindingFormGroup.controls['amount'].value + '' : undefined,
      })
      .subscribe(({ name, infinite, needsTalc, price, amount, priceRanges }) => {
        this.snackBar.open('با موفقیت بروزرسانی شد', '', {
          duration: 1500,
        });

        this.bindingFormGroup.markAsPristine();
        this.bindingFormGroup.patchValue({
          name,
          price,
          amount,
          infinite,
          needsTalc,
        });
        if (priceRanges.length > 0) {
          this.priceRanges.clear();
          priceRanges.forEach(range => {
            this.priceRangeFormArray.push(this.createPriceRange(range));
          });
        }

      });
  }
  updateBindingSalaryFee() {
    this.subsink.sink = this.stockService
      .updateBindingSaralyFee({
        ...this.bindingSalaryFeeFormGroup.value,
      })
      .subscribe(({ bindSalaryFee }) => {
        this.snackBar.open('با موفقیت بروزرسانی شد', '', {
          duration: 1500,
        });

        // this.bindingSalaryFeeFormGroup.markAsPristine();
        // this.bindingSalaryFeeFormGroup.patchValue({
        //   bindSalaryFee
        // });
      });
  }
  deleteBinding() {
    if (!this.pointer) {
      this.dialogReference.close('Pointer does not exists');
    }

    const id = parseInt(this.pointer!);
    this.subsink.sink = this.dialogBox
      .open(AreYouSureComponent)
      .afterClosed()
      .subscribe({
        next: (confirmed) => {
          if (!!confirmed) {
            this.subsink.sink = this.stockService
              .deleteBinding(this.pointer!)
              .subscribe({
                next: (result) => {
                  this.snackBar.open('صحافی با موفقیت حذف شد.', '', {
                    duration: 1500,
                  });
                },
              });
          }
        },
      });
  }

  public updateColor() {
    this.subsink.sink = this.stockService
      .updateTalcColor(this.pointer!, this.talcColorFormGroup.value)
      .subscribe({
        next: ({ name }) => {
          this.snackBar.open(`رنگ طلق ${name} با موفقیت به روز رسانی شد`);
        },
      });
  }


  addColor() {

  }
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }
}
